import { ReactElement, ReactNode, useCallback } from 'react';
import { useUITranslation } from '../../hooks';
import styles from './ui-section.module.scss';
import { UIText } from '../ui-text';
import { UIButton, UIButtonProps } from '../ui-button';
import { charcoal, concrete } from '../../styles';
import {
    UIErrorState,
    UIIcon,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIMenuItemProps,
    UIPopover,
    UIPressableAreaButtonLess,
    UITooltip
} from '../..';

export type UISectionProps = {
    fill?: boolean;
    children?: ReactNode;
};

export function UISection(props: UISectionProps): ReactElement {
    const { fill = false, children } = props;

    return (
        <div className={styles.container} style={fill ? { flex: 1 } : {}}>
            {children}
        </div>
    );
}

export type UISectionHeaderProps = {
    title: string;
    subtitle?: string;
    rightButtons?: UIButtonProps[];
    rightMenuItemsText?: string;
    rightMenuItems?: UIMenuItemProps[];
};

export function UISectionHeader(props: UISectionHeaderProps): ReactElement {
    const { t } = useUITranslation();
    const {
        title,
        subtitle,
        rightButtons = [],
        rightMenuItemsText = t('shared.labels.edit'),
        rightMenuItems = []
    } = props;

    const headerClassNames = [styles.header];
    if (subtitle) {
        headerClassNames.push(styles.headerWithSubtitle);
    }

    return (
        <div className={headerClassNames.join(' ')}>
            <div className={styles.headerLeft}>
                <UIText variant="h3">{title}</UIText>
                {subtitle && (
                    <UIText type="default2" variant="small">
                        {subtitle}
                    </UIText>
                )}
            </div>
            <div className={styles.headerRight}>
                {rightButtons.map((buttonProps, index) => (
                    <UIButton key={index} {...buttonProps} />
                ))}
                {rightMenuItems.length > 0 && (
                    <UIPopover
                        placement="bottom_left"
                        popoverPadding={4}
                        trigger="click"
                        triggerNode={
                            <button className={styles.removeButtonStyle}>
                                <UIPressableAreaButtonLess
                                    verticalPadding={6}
                                    horizontalPadding={10}
                                >
                                    <UIText>{rightMenuItemsText}</UIText>
                                    <UIIcon
                                        name={'chevron-down'}
                                        size={18}
                                        color={charcoal}
                                    />
                                </UIPressableAreaButtonLess>
                            </button>
                        }
                    >
                        <UIMenu>
                            {rightMenuItems.map((menuItemProps, index) => (
                                <UIMenuItem key={index} {...menuItemProps} />
                            ))}
                        </UIMenu>
                    </UIPopover>
                )}
            </div>
        </div>
    );
}

type UISectionTableHeaderItem = {
    text?: string;
    tooltip?: string;
    items?: UISectionTableHeaderItem[];
};
export type UISectionTableHeaderProps = {
    items: UISectionTableHeaderItem[];
};

export function UISectionTableHeader(
    props: UISectionTableHeaderProps
): ReactElement {
    const { items } = props;

    const renderItem = useCallback(
        (item: UISectionTableHeaderItem, index: number) => {
            return (
                <div
                    style={{
                        display: 'flex',
                        flex: 1
                    }}
                    key={index}
                >
                    {item.text ? (
                        <div
                            style={{
                                display: 'flex',
                                gap: 4,
                                alignItems: 'center'
                            }}
                        >
                            <UIText>{item.text}</UIText>
                            {item.tooltip && (
                                <UITooltip text={item.tooltip} place="right">
                                    <UIIcon
                                        name="help-circle"
                                        size={12}
                                        color={charcoal}
                                    />
                                </UITooltip>
                            )}
                        </div>
                    ) : item.items ? (
                        item.items.map((item, index) => renderItem(item, index))
                    ) : null}
                </div>
            );
        },
        []
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 16,
                padding: 16,
                borderBottom: `1px solid ${concrete}`
            }}
        >
            {items.map((item, index) => renderItem(item, index))}
        </div>
    );
}

export type UISectionContentProps = {
    children?: ReactNode;
    borderBottom?: boolean;
};
export function UISectionContent(props: UISectionContentProps): ReactElement {
    const { children, borderBottom = false } = props;

    return (
        <div
            className={styles.content}
            style={
                borderBottom
                    ? {
                          borderBottom: `1px solid ${concrete}`
                      }
                    : {}
            }
        >
            {children}
        </div>
    );
}

export type UISectionStateProps = {
    title: string;
    status: 'error' | 'success' | 'pending';
    errorText?: string;
    onRetry?: () => void;
};

/**
 * A section that stands in for its own content while the data behind it is
 * loading or has failed. Rendered *instead of* the real section rather than
 * wrapping it, so the caller keeps the early return — and the type narrowing
 * that comes with it — over its loaded data.
 *
 * Anything other than `error` shows the loading state: the caller only reaches
 * this component when it has no data to render.
 */
export function UISectionState(props: UISectionStateProps): ReactElement {
    const { title, status, errorText, onRetry } = props;

    const { t } = useUITranslation();

    return (
        <UISection>
            <UISectionHeader title={title} />
            <UISectionContent>
                {status === 'error' ? (
                    <UIErrorState
                        title={errorText ?? t('shared.errors.generic')}
                        onRetry={onRetry}
                    />
                ) : (
                    <UILoadingState />
                )}
            </UISectionContent>
        </UISection>
    );
}

export type UISectionFooterProps = {
    actions: UIButtonProps[];
};
export function UISectionFooter(props: UISectionFooterProps): ReactElement {
    const { actions } = props;

    return (
        <div className={styles.footer}>
            {actions.map((actionProps, index) => (
                <UIButton key={index} {...actionProps} />
            ))}
        </div>
    );
}
